import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Observable, of, Subscription } from "rxjs";
import { RangeFeature } from "src/app/model/catalog/catalog.model";
import { LensSide} from "src/app/model/configuration/configuration";
import { LensOrderItem } from "src/app/model/order/order";
import { GlobalStorageService } from "src/app/service-utils/global-storage.service";
import { i18nService } from "src/app/service-utils/i18n.service";
import { ValidatorService } from "src/app/service-utils/validator-service";
import { NewOrderStoreService } from "../../new-order-store-service.service";

@Component({
    selector: 'app-trial-lens',
    templateUrl: './trial-lens.component.html',
    styleUrls: ['./trial-lens.component.scss']
})
export class TrialLensComponent implements OnInit, OnDestroy {

  @Input() side: LensSide;
  @Input() originalMaterial: string[];
  @Output() copyEmit = new EventEmitter<LensSide>();
  @Output() switchEmit = new EventEmitter<LensSide>();

  form: FormGroup;
  LensSide = LensSide;

  originalBackSide: string[] = [NONE, 'SPH', 'RT', 'BT', 'BTC', 'BTX', 'VPT', 'VPT-LENTI', 'MAC', 'T'];
  filteredBackSide: Observable<string[]> = of(this.originalBackSide);
  selectedBackSide: string;

  filteredMaterial: Observable<string[]>;

  isFormReInit = true;

  copyTrialLens: LensOrderItem;
  isCopyTrialLens: boolean = false;

  subscriptions = new Subscription();

  radiusBaseCurve = new RangeFeature('RadiusBasecurve', true, 3, 15, 0.05);
  radiusBaseCurve2 = new RangeFeature('RadiusBaseCurve2', true, 3, 15, 0.05);
  sphere = new RangeFeature('Sphere', true, -50, 50, 0.25);
  dioptreSteep = new RangeFeature('DioptreSteep', true, -50, 50, 0.25);
  axisComplete = new RangeFeature('AxisComplete', true, 0, 179.9, 0.1);
  cylinder = new RangeFeature('Cylinder', true, -20, 20, 0.25);
  axisCylinder = new RangeFeature('AxisCylinder', true, 0, 179, 1);

  constructor(private formBuilder: FormBuilder,
              private i18nService: i18nService,
              private newOrderStoreService: NewOrderStoreService,
              private validatorService: ValidatorService,
              private globalStorageService: GlobalStorageService) {}

  ngOnInit() {
    this.filteredMaterial = of(this.originalMaterial);

    this.form = this.formBuilder.group({
      backSide: ["", [this.validatorService.requiredFieldValidator('LensBackSide')]],
      material: ["", [this.validatorService.requiredFieldValidator('Material')]],
      RadiusBasecurve: [{value: '', disabled: true}, [this.validatorService.numericValidator('RadiusBasecurve'), this.validatorService.requiredFieldValidator('RadiusBasecurve'), this.validatorService.rangeValidator(this.radiusBaseCurve)]],
      RadiusBaseCurve2: [{value: '', disabled: true}, [this.validatorService.numericValidator('RadiusBaseCurve2'), this.validatorService.requiredFieldValidator('RadiusBaseCurve2'), this.validatorService.rangeValidator(this.radiusBaseCurve2)]],
      Sphere: [{value: '', disabled: true}, [this.validatorService.numericValidator('Sphere'), this.validatorService.requiredFieldValidator('Sphere'), this.validatorService.rangeValidator(this.sphere)]],
      DioptreSteep: [{value: '', disabled: true}, [this.validatorService.numericValidator('DioptreSteep'), this.validatorService.requiredFieldValidator('DioptreSteep'), this.validatorService.rangeValidator(this.dioptreSteep)]],
      AxisComplete: [{value: '', disabled: true}, [this.validatorService.numericValidator('AxisComplete'), this.validatorService.requiredFieldValidator('AxisComplete'), this.validatorService.rangeValidator(this.axisComplete)]],
      Cylinder: [{value: '', disabled: true}, [this.validatorService.numericValidator('Cylinder'), this.validatorService.requiredFieldValidator('Cylinder'), this.validatorService.rangeValidator(this.cylinder)]],
      AxisCylinder: [{value: '', disabled: true}, [this.validatorService.numericValidator('AxisCylinder'), this.validatorService.requiredFieldValidator('AxisCylinder'), this.validatorService.rangeValidator(this.axisCylinder)]],
    });

    this.initFilters();

    if(this.side === LensSide.RIGHT) {
      if(this.newOrderStoreService.getCopyTrialLensRight()) {
        this.copyTrialLens = this.newOrderStoreService.getCopyTrialLensRight();
        this.copyFormTrialLens();
      }
      this.subscriptions.add(this.newOrderStoreService.copyTrialLensRight$.subscribe(data => {
        if(data) {
          this.copyTrialLens = data;
          this.copyFormTrialLens();
        }
      }));
    }

    if(this.side === LensSide.LEFT) {
      if(this.newOrderStoreService.getCopyTrialLensLeft()) {
        this.copyTrialLens = this.newOrderStoreService.getCopyTrialLensLeft();
        this.copyFormTrialLens();
      }
      this.subscriptions.add(this.newOrderStoreService.copyTrialLensLeft$.subscribe(data => {
        if(data) {
          this.copyTrialLens = data;
          this.copyFormTrialLens();
        }
      }));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private copyFormTrialLens() {
    this.form.controls['backSide'].setValue(this.copyTrialLens.article.backSide.name);
    this.selectBackSide();
    this.form.controls['material'].setValue(this.copyTrialLens.material.name);
    this.selectMaterial();

    Object.keys(this.form.controls).forEach(key => {
      if(this.form.controls[key].enabled) {
        if(Object.keys(this.copyTrialLens.featureValues).find(featureKey => featureKey === key)) {
          this.setValueWithFormatCopyTrialLens(key, this.copyTrialLens.featureValues[key]);
        }
      }
    })
  }

  selectBackSide() {
    if(this.form.get('backSide').value === NONE) {
      this.form.get('backSide').setValue('');
    }
    this.selectedBackSide = this.form.get('backSide').value;
    this.filteredBackSide = of(this.originalBackSide);

    this.form.get('RadiusBasecurve').disable();
    this.form.get('RadiusBaseCurve2').disable();
    this.form.get('Sphere').disable();
    this.form.get('DioptreSteep').disable();
    this.form.get('AxisComplete').disable();
    this.form.get('Cylinder').disable();
    this.form.get('AxisCylinder').disable();

    if(this.selectedBackSide !== 'T' && this.selectedBackSide !== '') {
      this.form.get('RadiusBasecurve').enable();
      this.form.get('Sphere').enable();
      if(this.selectedBackSide === 'RT' || this.selectedBackSide === 'BT' || this.selectedBackSide === 'BTC' || this.selectedBackSide === 'BTX') {
        this.form.get('RadiusBaseCurve2').enable();
      }
      if(this.selectedBackSide === 'BT' || this.selectedBackSide === 'BTC' || this.selectedBackSide === 'BTX') {
        this.form.get('DioptreSteep').enable();
      }
      if(this.selectedBackSide === 'BTX') {
        this.form.get('AxisComplete').enable();
      }
      if(this.selectedBackSide === 'VPT' || this.selectedBackSide === 'VPT-LENTI') {
        this.form.get('Cylinder').enable();
        this.form.get('AxisCylinder').enable();
      }
    }
  }

  selectMaterial() {
    if(this.form.get('material').value === NONE) {
      this.form.get('material').setValue('');
    }
    this.filteredMaterial = of(this.originalMaterial);
  }

  private initFilters() {

    this.form.get('material').valueChanges.subscribe(value => {
      if(value)
        this.filterMaterial(value);
    });

    this.form.get('backSide').valueChanges.subscribe(value => {
      if(value)
        this.filterBackSide(value);
    });

    this.form.get('material').valueChanges.subscribe(() => {
      if(this.side === LensSide.LEFT) {
        this.newOrderStoreService.setMaterialTrialLensLeft(this.form.get('material').value);
      } else if(this.side === LensSide.RIGHT) {
        this.newOrderStoreService.setMaterialTrialLensRight(this.form.get('material').value);
      }
    });

    this.initRadiusBasecurve();
    this.initRadiusBasecurve2();
  }

  private initRadiusBasecurve() {
    this.form.get('RadiusBasecurve').valueChanges.subscribe(() => {
      if(this.side === LensSide.LEFT) {
        this.newOrderStoreService.setRadiusBasecurveTrialLensLeft(this.form.get('RadiusBasecurve').value);
        if(this.selectedBackSide !== 'RT' && this.selectedBackSide !== 'BT' && this.selectedBackSide !== 'BTX' && this.selectedBackSide !== 'BTC') {
          if(this.selectedBackSide === 'MAC' && this.form.get('RadiusBasecurve').value && this.form.get('RadiusBasecurve').value !== '') {
            this.newOrderStoreService.setRadiusBasecurve2TrialLensLeft(this.i18nService.replaceFromDotToComma(this.i18nService.replaceFromCommaToDot(this.form.get('RadiusBasecurve').value) - 0.35));
          } else {
            this.newOrderStoreService.setRadiusBasecurve2TrialLensLeft(this.form.get('RadiusBasecurve').value);
          }
        }
      } else if(this.side === LensSide.RIGHT) {
        this.newOrderStoreService.setRadiusBasecurveTrialLensRight(this.form.get('RadiusBasecurve').value);
        if(this.selectedBackSide !== 'RT' && this.selectedBackSide !== 'BT' && this.selectedBackSide !== 'BTX' && this.selectedBackSide !== 'BTC') {
          if(this.selectedBackSide === 'MAC' && this.form.get('RadiusBasecurve').value && this.form.get('RadiusBasecurve').value !== '') {
            this.newOrderStoreService.setRadiusBasecurve2TrialLensRight(this.i18nService.replaceFromDotToComma(this.i18nService.replaceFromCommaToDot(this.form.get('RadiusBasecurve').value) - 0.35));
          } else {
            this.newOrderStoreService.setRadiusBasecurve2TrialLensRight(this.form.get('RadiusBasecurve').value);
          }
        }
      }
    });
  }

  private initRadiusBasecurve2() {
    this.form.get('RadiusBaseCurve2').valueChanges.subscribe(() => {
      if(this.side === LensSide.LEFT) {
        this.newOrderStoreService.setRadiusBasecurve2TrialLensLeft(this.form.get('RadiusBaseCurve2').value);
      } else if(this.side === LensSide.RIGHT) {
        this.newOrderStoreService.setRadiusBasecurve2TrialLensRight(this.form.get('RadiusBaseCurve2').value);
      }
    });
  }

  private filterBackSide(value: any) {
      const filterValue = this.normalizeValue(value);

      let intermediateBackSides = this.originalBackSide.filter(backSide => this.normalizeValue(backSide).includes(filterValue));
      if(!intermediateBackSides.includes(NONE)) {
        intermediateBackSides.unshift(NONE);
      }
      this.filteredBackSide = of(intermediateBackSides);
  }

  private filterMaterial(value: any) {
      const filterValue = this.normalizeValue(value);

      let intermediateMaterial = this.originalMaterial.filter(material => this.normalizeValue(material).includes(filterValue));
      if(!intermediateMaterial.includes(NONE)) {
        intermediateMaterial.unshift(NONE);
      }
      this.filteredMaterial = of(intermediateMaterial);
  }

  checkRequiredFields() {

    Object.keys(this.form.controls).forEach(key => {
      if(this.form.controls[key].enabled && this.form.controls[key].value === '') {
        this.form.controls[key].markAsTouched();
        this.form.controls[key].setValue("");
      }
    });

  }

  private normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  getLimitsFormatted(rangeMin: number, rangeMax: number) {
    return this.i18nService.getLimitsFormatted(rangeMin, rangeMax);
  }

  copy(side: LensSide) {
    this.copyEmit.emit(side);
  }

  switch(side: LensSide) {
      this.switchEmit.emit(side);
  }

  setValueWithFormat(featureName: string) {
    this.form.controls[featureName].setValue(
        this.i18nService.replaceFromDotToComma(
            this.i18nService.formatIfNumber(this.form.controls[featureName].value, this.globalStorageService.decimalFormats.find(feature => feature.featureName === featureName)?.decimalFormat)
        )
    );
  }

  setValueWithFormatCopyTrialLens(featureName: string, value: any) {
    this.form.controls[featureName].setValue(
        this.i18nService.replaceFromDotToComma(
            this.i18nService.formatIfNumber(value, this.globalStorageService.decimalFormats.find(feature => feature.featureName === featureName)?.decimalFormat)
        )
    );
  }
}

export const NONE = 'NONE';
